import { apiGet, apiPatch } from "./api";

const STORAGE_KEY = "omni:brandColor";
export const DEFAULT_BRAND_COLOR = "#6366f1";

export const COLOR_PRESETS: Array<{ name: string; color: string }> = [
  { name: "Índigo", color: DEFAULT_BRAND_COLOR },
  { name: "Azul", color: "#2563eb" },
  { name: "Ciano", color: "#0e7490" },
  { name: "Esmeralda", color: "#047857" },
  { name: "Violeta", color: "#7c3aed" },
  { name: "Rosa", color: "#db2777" },
  { name: "Laranja", color: "#c2410c" },
  { name: "Grafite", color: "#475569" },
];

type Rgb = [number, number, number];

const toRgb = (hex: string): Rgb => [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16)) as Rgb;
const toHex = (rgb: Rgb) => `#${rgb.map((value) => Math.round(value).toString(16).padStart(2, "0")).join("")}`;
const mix = (rgb: Rgb, target: number, amount: number): Rgb =>
  rgb.map((value) => value + (target - value) * amount) as Rgb;

export const isValidColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);

/** Contraste com o texto branco dos botões, de 1 a 21. Abaixo de 3 o texto fica difícil de ler. */
export const contrastWithWhite = (hex: string) => {
  const [r, g, b] = toRgb(hex).map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 1.05 / (0.2126 * r + 0.7152 * g + 0.0722 * b + 0.05);
};
export const MIN_CONTRAST = 3;

// Só as cores de destaque mudam; fundo e texto continuam vindo do tema claro ou escuro.
export const applyBrandColor = (color: string | null) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!color || !isValidColor(color) || color.toLowerCase() === DEFAULT_BRAND_COLOR) {
    TOKENS.forEach((token) => root.style.removeProperty(token));
    return;
  }
  const rgb = toRgb(color);
  const isLight = root.getAttribute("data-theme") === "light";
  const rgba = (alpha: number) => `rgba(${rgb.join(", ")}, ${alpha})`;
  const lighter = toHex(mix(rgb, 255, 0.35));
  const values: Record<string, string> = {
    "--brand": color,
    "--brand-hover": toHex(mix(rgb, 0, 0.14)),
    "--brand-soft": rgba(isLight ? 0.1 : 0.16),
    "--brand-text": isLight ? toHex(mix(rgb, 0, 0.2)) : toHex(mix(rgb, 255, 0.55)),
    "--accent": color,
    "--accent-strong": isLight ? color : lighter,
    "--accent-cool": isLight ? color : lighter,
    "--bubble-out": rgba(isLight ? 0.12 : 0.2),
    "--bubble-out-border": rgba(0.28),
    "--avatar-bg": rgba(isLight ? 0.12 : 0.2),
    "--avatar-text": isLight ? toHex(mix(rgb, 0, 0.2)) : toHex(mix(rgb, 255, 0.65)),
    "--ring": `0 0 0 3px ${rgba(0.35)}`,
  };
  Object.entries(values).forEach(([token, value]) => root.style.setProperty(token, value));
};

const TOKENS = [
  "--brand",
  "--brand-hover",
  "--brand-soft",
  "--brand-text",
  "--accent",
  "--accent-strong",
  "--accent-cool",
  "--bubble-out",
  "--bubble-out-border",
  "--avatar-bg",
  "--avatar-text",
  "--ring",
];

export const getStoredBrandColor = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const store = (color: string | null) => {
  try {
    if (color) window.localStorage.setItem(STORAGE_KEY, color);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sem localStorage a cor só não fica disponível antes de a API responder.
  }
};

// Aplica a cópia local na hora, sem piscar a cor padrão, e reaplica ao trocar entre claro e escuro.
export const initBrandColor = () => {
  applyBrandColor(getStoredBrandColor());
  new MutationObserver(() => applyBrandColor(getStoredBrandColor())).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
};

export const syncBrandColor = async () => {
  const data = await apiGet<{ brandColor: string | null }>("/theme");
  store(data?.brandColor ?? null);
  applyBrandColor(data?.brandColor ?? null);
  return data?.brandColor ?? null;
};

export const saveBrandColor = async (color: string | null) => {
  await apiPatch("/theme", { brandColor: color });
  store(color);
  applyBrandColor(color);
};
